import React, { useEffect } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import styled from "styled-components";
import InputField from "../../components/core-components/InputField.tsx";
import Button from "../../components/core-components/Button.tsx";
import { IoTrash } from "react-icons/io5";
import {
  addNewPrinter,
  deletePrinter,
  getPrinterDetails,
  updatePrinterSettings,
} from "../../../services/filamentService.js";
import { useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { dataActions } from "../../../store/index.js";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-bottom: 1rem;
  gap: 1rem;
  width: 100%;
  height: 100%;
`;

const PageTitle = styled.h2`
  color: var(--secondary-font-color);
  font-family: Georgia, serif;
  letter-spacing: 1px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.15);
  padding-bottom: 0.5rem;
`;

const Subtitle = styled.h4`
  color: var(--secondary-font-color);
  font-family: Georgia, serif;
  letter-spacing: 1px;
  padding-bottom: 0.5rem;
`;

const SectionTitle = styled.h4`
  margin: 0;
  color: var(--tertiary-font-color);
  font-family: Georgia, serif;
  letter-spacing: 1px;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.15);
`;
const DetailsContainer = styled.div`
  display: flex;
  flex-direction: row;

  align-items: end;
  gap: 1rem;

  & > div {
    max-width: 30%;
  }

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;
interface Props {
  isNewPrinter?: boolean;
}
interface OutletContextType {
  refetch: () => void;
}
const PrinterSettings: React.FC<Props> = ({ isNewPrinter }) => {
  const { printerId } = useParams();
  const dispach = useDispatch();
  const { refetch } = useOutletContext<OutletContextType>();
  const navigate = useNavigate();
  const { data: printer } = useQuery({
    queryKey: ["printers", printerId],
    queryFn: () => getPrinterDetails(printerId),
    enabled: !isNewPrinter && printerId !== "0",
  });

  const [printerName, setPrinterName] = React.useState(printer?.name || "");
  const [bedHeight, setBedHeight] = React.useState(printer?.bedHeight || "");
  const [bedWidth, setBedWidth] = React.useState(printer?.bedWidth || "");
  const [bedDepth, setBedDepth] = React.useState(printer?.bedDepth || "");
  const [maxNozzleTemp, setMaxNozzleTemp] = React.useState(
    printer?.maxNozzleTemp || ""
  );
  const [maxPrintingSpeed, setMaxPrintingSpeed] = React.useState(
    printer?.maxPrintingSpeed || ""
  );
  const [colorSlots, setColorSlots] = React.useState(printer?.colorSlots || "");
  const [printerImage, setPrinterImage] = React.useState<File | null>(null);
  const [showSaveButton, setShowSaveButton] = React.useState(false);
  useEffect(() => {
    if (isNewPrinter === false && printer) {
      setPrinterName(printer?.name);
      setBedHeight(printer?.bedHeight);
      setBedWidth(printer?.bedWidth);
      setBedDepth(printer?.bedDepth);
      setMaxNozzleTemp(printer?.maxNozzleTemp);
      setMaxPrintingSpeed(printer?.maxPrintingSpeed);
      setColorSlots(printer?.colorSlots);

      dispach(dataActions.addActivePrinter(printer));
    }
    if (isNewPrinter) {
      // Resetăm complet state-urile
      setPrinterName("");
      setBedHeight("");
      setBedWidth("");
      setBedDepth("");
      setColorSlots("");
      setMaxNozzleTemp("");
      setMaxPrintingSpeed("");
      setPrinterImage(null);
    }
  }, [
    isNewPrinter,
    printer,
    setBedDepth,
    setBedHeight,
    setBedWidth,
    setColorSlots,
    setMaxNozzleTemp,
    setMaxPrintingSpeed,
    setPrinterName,
  ]);

  useEffect(() => {
    if (!isNewPrinter) {
      if (
        maxNozzleTemp &&
        Number(maxNozzleTemp) > 0 &&
        colorSlots &&
        Number(colorSlots) > 0 &&
        printerName &&
        printerName !== "" &&
        (maxNozzleTemp !== printer?.maxNozzleTemp ||
          colorSlots !== printer?.colorSlots ||
          printerName !== printer?.name)
      ) {
        setShowSaveButton(true);
      } else {
        setShowSaveButton(false);
      }
    }
  }, [
    printer,
    isNewPrinter,
    printerName,
    colorSlots,
    maxNozzleTemp,
    setShowSaveButton,
  ]);
  const handleSave = async () => {
    if (isNewPrinter) {
      //salvam
      const printer = {
        Name: printerName,
        BedHeight: bedHeight,
        BedWidth: bedWidth,
        BedDepth: bedDepth,
        ColorSlots: colorSlots,
        MaxNozzleTemp: maxNozzleTemp,
        MaxPrintingSpeed: maxPrintingSpeed,
        PrinterImage: printerImage,
      };

      try {
        const createdPrinter = await addNewPrinter(printer);
        const newId = createdPrinter.printerID;
        refetch && refetch();
        navigate(`/backoffice/printer/${newId}`);
      } catch (error) {
        alert(
          "A apărut o eroare la adăugarea imprimantei. Te rugăm să încerci din nou mai târziu."
        );
      }
    } else {
      //altfel, actualizam
      try {
        await updatePrinterSettings(
          printerId,
          printerName,
          colorSlots,
          maxNozzleTemp
        );
      } catch (error) {
        alert(
          "A apărut o eroare la update-ul imprimantei. Te rugăm să încerci din nou mai târziu."
        );
      }
    }
  };

  const handleDelete = async () => {
    try {
      await deletePrinter(printerId);

      navigate(`/backoffice/printer/0`);
      refetch && refetch();
    } catch (error) {
      alert(
        "A apărut o eroare la stergerea imprimantei. Te rugăm să încerci din nou mai târziu."
      );
    }
  };

  useEffect(() => {
    if (isNewPrinter) {
      if (
        maxNozzleTemp &&
        Number(maxNozzleTemp) > 0 &&
        colorSlots &&
        Number(colorSlots) > 0 &&
        printerName &&
        printerName !== "" &&
        bedHeight &&
        bedDepth &&
        bedDepth !== "0" &&
        bedWidth &&
        maxPrintingSpeed
      ) {
        setShowSaveButton(true);
      } else {
        setShowSaveButton(false);
      }
    }
  }, [
    isNewPrinter,
    printerName,
    colorSlots,
    maxNozzleTemp,
    bedHeight,
    bedWidth,
    bedDepth,
    maxPrintingSpeed,
    setShowSaveButton,
  ]);
  if (!isNewPrinter && (!printerId || printerId === "0"))
    return (
      <Wrapper data-testid="printer-settings">
        <Subtitle>Alege o imprimantă pentru a vedea detalii</Subtitle>
      </Wrapper>
    );

  return (
    <Wrapper data-testid="printer-settings">
      <PageTitle>
        {isNewPrinter ? "Imprimantă nouă" : "Setări imprimantă"}
      </PageTitle>
      <SectionTitle>General</SectionTitle>

      <DetailsContainer>
        <InputField
          name="name"
          type="text"
          label={"Nume"}
          value={printerName}
          onChange={(e) => {
            setPrinterName(e.target.value);
          }}
          placeholder={"Nume"}
        />
        <InputField
          name="colors"
          type="number"
          label={"Sloturi culori"}
          value={colorSlots}
          onChange={(e) => {
            setColorSlots(e.target.value);
          }}
          placeholder={"1"}
        />

        {isNewPrinter ? (
          <div>
            <input
              type="file"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  setPrinterImage(e.target.files[0]);
                }
              }}
            />
          </div>
        ) : (
          <>
            <InputField
              name="id"
              type="number"
              label={"Id imprimantă"}
              value={printer?.printerID}
              onChange={(e) => {}}
              placeholder={"1"}
              readonly
            />
            <Button
              id="delete-printer-button"
              onClick={() => {
                const confirmDelete = window.confirm(
                  "Ești sigur că vrei să ștergi imprimanta? Acaesta acțiune nu poate fi anulată ulterior, iar toate datele vor fi sterse!"
                );
                if (confirmDelete) {
                  handleDelete();
                }
              }}
              icon={<IoTrash />}
              backgroundColor={"#fbe4e4"}
              color="#951717"
            />
          </>
        )}
      </DetailsContainer>
      <SectionTitle>Dimensiuni</SectionTitle>
      <DetailsContainer>
        <InputField
          name="width"
          type="number"
          label={"Latime /mm"}
          value={bedWidth}
          onChange={(e) => {
            setBedWidth(e.target.value);
          }}
          placeholder={"30 mm"}
          readonly={!isNewPrinter}
        />

        <InputField
          name="depth"
          type="number"
          label={"Adancime /mm"}
          value={bedDepth}
          onChange={(e) => {
            setBedDepth(e.target.value);
          }}
          placeholder={"30 mm"}
          readonly={!isNewPrinter}
        />
        <InputField
          name="height"
          type="number"
          label={"Inaltime /mm"}
          value={bedHeight}
          onChange={(e) => {
            setBedHeight(e.target.value);
          }}
          placeholder={"90 mm"}
          readonly={!isNewPrinter}
        />
      </DetailsContainer>
      <SectionTitle>Valori maxime</SectionTitle>
      <DetailsContainer>
        <InputField
          name="maxNozzleTemp"
          type="number"
          label={"Max. temperatura duză"}
          value={maxNozzleTemp}
          onChange={(e) => {
            setMaxNozzleTemp(e.target.value);
          }}
          placeholder={"250 °C"}
        />

        <InputField
          name="maxPrintingSpeed"
          type="number"
          label={"Max. viteza printare"}
          value={maxPrintingSpeed}
          onChange={(e) => {
            setMaxPrintingSpeed(e.target.value);
          }}
          placeholder={"100 mm/s"}
          readonly={!isNewPrinter}
        />
      </DetailsContainer>
      {showSaveButton && (
        <Button
          id="save-printer-button"
          onClick={handleSave}
          text={"Save"}
          backgroundColor={"var(--secondary-green)"}
          color="white"
          isFullWidth
        />
      )}
    </Wrapper>
  );
};

export default PrinterSettings;
